import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Clock, Users, Play, Loader2 } from 'lucide-react';
import { coursesApi } from '../../api';
import { Button } from '../ui/button';
import DashboardLayout from './DashboardLayout';
import CreateCourseModal from '../modals/CreateCourseModal';

const Courses = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [tab, setTab] = useState('all');  // 'all' = opublikowane, 'mine' = moje (też szkice)

  const fetchCourses = async () => {
    setLoading(true);
    try {
      if (tab === 'mine') {
        const r = await coursesApi.getMy();
        setCourses(r.data?.items || []);
      } else {
        const r = await coursesApi.list(0, 50);
        setCourses(Array.isArray(r.data) ? r.data : (r.data?.items || []));
      }
    } catch (error) {
      console.error('Failed to fetch courses:', error);
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCourses(); }, [tab]);

  const handleCourseCreated = (newCourse) => {
    if (newCourse?.id) navigate(`/dashboard/courses/${newCourse.id}`);
  };

  const filteredCourses = courses.filter(course =>
    course.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <DashboardLayout>
      <CreateCourseModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCourseCreated}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('courses.title')}</h1>
          <p className="text-gray-500 mt-1">{t('courses.subtitle')}</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          {t('courses.create_btn')}
        </Button>
      </div>

      {/* Tabs + Search */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
          <button
            onClick={() => setTab('all')}
            className={`px-3 py-1.5 text-sm rounded-md ${tab === 'all' ? 'bg-white shadow-sm font-medium text-slate-900' : 'text-slate-500'}`}
          >
            Wszystkie
          </button>
          <button
            onClick={() => setTab('mine')}
            className={`px-3 py-1.5 text-sm rounded-md ${tab === 'mine' ? 'bg-white shadow-sm font-medium text-slate-900' : 'text-slate-500'}`}
          >
            Moje kursy
          </button>
        </div>
        <div className="relative sm:max-w-xs flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder={t('common.search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      ) : (
        <>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCourses.map((course) => (
              <div
                key={course.id}
                onClick={() => navigate(`/dashboard/courses/${course.id}`)}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow group cursor-pointer"
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={course.image || 'https://images.unsplash.com/photo-1501504905252-473c47e087f8?w=400&q=80'}
                    alt={course.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  {course.status && course.status !== 'published' && (
                    <span className="absolute top-2 left-2 text-[11px] px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700">
                      Szkic
                    </span>
                  )}
                  <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center">
                      <Play className="w-6 h-6 text-emerald-600 fill-emerald-600 ml-1" />
                    </div>
                  </div>
                </div>

                <div className="p-5">
                  <h3 className="font-semibold text-gray-900 group-hover:text-emerald-600 transition-colors line-clamp-1 mb-2">
                    {course.title}
                  </h3>
                  <p className="text-sm text-gray-500 line-clamp-2 mb-4">
                    {course.description}
                  </p>

                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-4 h-4" />
                      <span>{course.total_duration_minutes || 0} min</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Users className="w-4 h-4" />
                      <span>{(course.students_count || 0).toLocaleString()}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                    <span className="text-sm text-gray-600">{course.lessons_count || 0} lekcji</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {course.price ? `$${course.price}` : 'Darmowy'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredCourses.length === 0 && (
            <div className="text-center py-12">
              <Play className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {tab === 'mine' ? 'Nie masz jeszcze kursów' : 'Brak kursów'}
              </h3>
              <p className="text-gray-500">
                {tab === 'mine' ? 'Utwórz pierwszy kurs przyciskiem powyżej' : 'Spróbuj zmienić wyszukiwanie'}
              </p>
            </div>
          )}
        </>
      )}
    </DashboardLayout>
  );
};

export default Courses;
